import React, { useCallback, useState } from 'react'
import styled, { css } from 'styled-components'
import { useRouter } from 'next/router'
import { observer } from 'mobx-react'
import SharePanel from 'components/SharePanel'
import ArrowBackIosRoundedIcon from '@material-ui/icons/ArrowBackIosRounded'
import SuccessSnack from 'components/UI/SuccessSnack'

import { COUPON_PROJECT } from 'constants/coupon'
import useCanShare from 'hooks/useCanShare'
import useSdk from 'hooks/useSdk'
import { useUserStore } from 'store/userStore'
import { useUserAgentStore } from 'store/userAgentStore'
import { useShareStore } from 'store/shareStore'
import { openLinkBlank } from 'utils/appSchemeUtil'
import { permaLinkHashQueryString } from 'utils/urlUtil'
import { useTrackingStore } from 'store/trackingStore'
import { COUPON_SCREEN_NAME, EVENT_ACTION, BUTTON_NAME } from 'tracking'

import CategoryBar from './CategoryBar'
import MobileSearchInput from './MobileSearchInput'
import IconCoupon from '/static/images/logo-coupon.svg'
import IconHk01Logo from '/static/images/ic-hk01-logo.svg'
import IconSharePage from '/static/images/ic-share-page.svg'
import IconSearchOn from '/static/images/ic-search-page-on.svg'
import IconSearchOff from '/static/images/ic-search-page-off.svg'

const MobileCouponHeader = observer(({ isInAppWebView }) => {
  const router = useRouter()
  const { pathname, asPath, query } = router
  const userAgentStore = useUserAgentStore()
  const isIOS = userAgentStore.isIOS()
  const { trackEvent, isInited } = useTrackingStore()
  const sdk = useSdk()
  const canShare = useCanShare()
  const { shareContentPayload } = useShareStore()
  const [openShareAction, setOpenShareAction] = useState(false)
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [showSearchBar, setShowSearchBar] = useState(!!query.searchKey)

  const hasSharePanel = !(isInAppWebView || canShare)
  const shareUrl = permaLinkHashQueryString(
    asPath,
    COUPON_PROJECT.coupon_web,
    'fast'
  )

  const closeShareAction = useCallback(() => setOpenShareAction(false), [])

  const onSharePage = useCallback(async () => {
    const { title, message } = shareContentPayload
    if (hasSharePanel) {
      return setOpenShareAction(true)
    }
    if (window.navigator.share) {
      const shareData = {
        title: pathname === '/sku' ? title : '01優惠券',
        text:
          pathname === '/sku'
            ? message
            : '分享「01優惠券」俾朋友，著數一齊享！\n',
        url: shareUrl,
      }
      const screenName = pathname.substr(1).toUpperCase()
      const itemType = pathname.includes('sku') ? 'sku' : 'coupon'
      if (isInited) {
        trackEvent(EVENT_ACTION.CLICK, {
          screen: COUPON_SCREEN_NAME[screenName],
          button: BUTTON_NAME.SHARE,
          item_id: query.id || null,
          item_type: itemType,
          share_option: JSON.stringify(shareData),
        })
      }
      try {
        await navigator.share(shareData)
        if (isInited) {
          trackEvent(EVENT_ACTION.RESPONSE, {
            screen: COUPON_SCREEN_NAME[screenName],
            button: BUTTON_NAME.SHARE,
            item_type: itemType,
            share_option: JSON.stringify(shareData),
            result: 'share success',
          })
        }
      } catch (err) {
        trackEvent(EVENT_ACTION.RESPONSE, {
          screen: COUPON_SCREEN_NAME[screenName],
          button: BUTTON_NAME.SHARE,
          item_type: itemType,
          share_option: JSON.stringify(shareData),
          result: JSON.stringify(err),
        })
        if (isIOS) {
          // best effort
          window.location.reload()
        }
      }
    }

    if (!sdk) return

    const { EVENT_ENUMS, requestAndWait, isSupportedApp } = sdk
    if (isSupportedApp()) {
      await requestAndWait({
        type: EVENT_ENUMS.OPEN_SHARE_DIALOG,
        payload: {
          message,
          url: shareUrl,
        },
      }).catch((err) => {
        console.warn(err)
      })
    }
  }, [
    shareContentPayload,
    hasSharePanel,
    sdk,
    shareUrl,
    pathname,
    isInited,
    trackEvent,
    query.id,
    isIOS,
  ])

  const { isLoggedIn } = useUserStore()
  const tabPages = ['/skus', '/mycoupon', '/settings']
  const isSettings = pathname === '/settings'
  const isTabPage = tabPages.includes(pathname)
  const showCategoryBar =
    (pathname === '/skus' && !showSearchBar) ||
    (pathname === '/mycoupon' && isLoggedIn && !showSearchBar)

  const goHk01 = () => {
    openLinkBlank('https://www.hk01.com')
  }
  const goHome = () => {
    router.push('/skus')
  }
  const hasShareUtils = ['/sku', '/skus', '/userguide'].includes(pathname)
  // TODO: Further Refactor
  const ShareUtilities = () => {
    const copyLink = useCallback(() => {
      setSnackbarOpen(true)
      closeShareAction()
    }, [])
    const closeSnackbar = useCallback(() => setSnackbarOpen(false), [])

    if (hasShareUtils) {
      return (
        <>
          <IconSharePage onClick={onSharePage} />
          {hasSharePanel && (
            <>
              <SharePanel
                shareUrl={shareUrl}
                openShareAction={openShareAction}
                closeShareAction={closeShareAction}
                copyLink={copyLink}
                router={router}
              />
              <SuccessSnack
                openSnackbar={snackbarOpen}
                successText={'鏈接複製成功!'}
                vertical="bottom"
                closeSnackbar={closeSnackbar}
              />
            </>
          )}
        </>
      )
    }
    return <ShareSpacing />
  }

  const SearchIconButton = () => {
    return (
      <SearchButton>
        {showSearchBar ? (
          <IconSearchOn />
        ) : (
          <IconSearchOff onClick={() => setShowSearchBar(true)} />
        )}
      </SearchButton>
    )
  }
  if (isTabPage) {
    return (
      <>
        <HeaderTop>
          <IconHk01Logo onClick={goHk01} />
          <IconCoupon onClick={goHome} />
          <div>
            {pathname === '/skus' && <SearchIconButton />}
            <ShareUtilities />
          </div>
        </HeaderTop>
        {!isSettings && (
          <>
            {showSearchBar && (
              <MobileSearchInput
                trackEvent={trackEvent}
                hideSearchBar={() => setShowSearchBar(false)}
                showSearchBar={showSearchBar}
              />
            )}
            <CategoryBar
              hiddenNavigationButtons
              showCategoryBar={showCategoryBar}
            />
          </>
        )}
      </>
    )
  }
  // TODO: further refactor this block by breaking down into smaller components
  const arrowStyle = {
    color: '#3b3b3b',
    fontSize: 20,
    position: 'absolute',
    height: '100%',
    width: 24,
  }
  const headerTitle = {
    '/sku': '優惠券詳情',
    '/coupon': '我的優惠券',
    '/userguide': '添加01優惠券到主畫面',
  }
  const historyBack = () => {
    if (window.history.length > 1) {
      window.history.go(-1)
    } else {
      goHome()
    }
  }
  return (
    <HeaderTop>
      <ArrowBackIosRoundedIcon style={arrowStyle} onClick={historyBack} />
      <HeaderTitle offsetLeft={hasShareUtils}>
        {headerTitle[pathname]}
      </HeaderTitle>
      <ShareUtilities />
    </HeaderTop>
  )
})

const HeaderTop = styled.div`
  height: 44px;
  background-color: ${(p) => p.theme.color.white};
  box-shadow: 0 2px 23px 0 #e4e6e9;
  padding: 0 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  z-index: 10;
  position: sticky;
  top: 0;
`
const HeaderTitle = styled.h3`
  color: ${(p) => p.theme.color.dark100};
  font-size: 16px;
  font-weight: normal;
  text-align: center;
  margin: 0 auto;
  letter-spacing: 1px;
  ${(p) =>
    p.offsetLeft &&
    css`
      padding-left: 24px;
    `}
`

const ShareSpacing = styled.div`
  width: 24px;
  height: 24px;
  padding: 0;
  margin: 0;
`
const SearchButton = styled.div`
  position: absolute;
  right: 56px;
`
export default MobileCouponHeader
